/**
 * Searching a project's vector index, as decisions — UX-05.
 *
 * WHAT THIS PAGE CAN AND CANNOT DO
 *
 * Vectorize has no "list everything". There is no count, no browse, no way
 * to see what is in an index except by searching it. So this page is a
 * search box, and the honest thing it must do is say what it cannot tell
 * you rather than imply an answer it does not have.
 *
 * THE AMBIGUITY THAT MATTERS
 *
 * An empty result means one of two things and the client cannot tell which:
 *
 *   nothing is indexed          the search was fine, the index is empty
 *   nothing matched closely     there is content, just not that
 *
 * Every other empty state in this dashboard can distinguish its cases. This
 * one genuinely cannot, so it says so - because somebody who assumes the
 * wrong one debugs the wrong thing, and the two fixes are "index your
 * documents" and "try different words".
 *
 * WHAT A SCORE IS NOT
 *
 * A percentage. Scores are relative to the index and the model; 0.81 is not
 * "81% relevant" and is not comparable to 0.81 from another project. The
 * page shows them because their ORDER is the answer, and labels them in a
 * way that does not invite the other reading.
 */

export type VectorMatch = {
  id: string
  score: number
  metadata: Record<string, unknown> | null
}

export type SearchState =
  | { kind: 'idle' }
  | { kind: 'empty'; query: string }
  | { kind: 'matches'; matches: VectorMatch[] }

export function searchState(input: { searched: string; matches: VectorMatch[] }): SearchState {
  if (input.searched === '') return { kind: 'idle' }
  if (input.matches.length === 0) return { kind: 'empty', query: input.searched }
  return { kind: 'matches', matches: input.matches }
}

export function emptyMessage(state: SearchState): string {
  if (state.kind !== 'empty') return ''
  return `Nothing in this index is close to “${state.query}”. That means one of two things, `
    + 'and there is no way to tell them apart from here: either nothing has been indexed '
    + 'yet, or there is content and none of it is about this.'
}

/** What to do about it, given that we cannot say which case it is. */
export const EMPTY_NEXT_STEPS = [
  'If you have not indexed anything yet: `nb.vectors.upsert({ id, text })` from your code, '
    + 'or `nimbly vectors add` from a terminal.',
  'If you have: try the words somebody would actually use. Search here is by meaning, so '
    + '“how long for my money back” should find a document about refunds.',
]

/**
 * Said next to the scores.
 *
 * Without it somebody reads 0.81 as a percentage, concludes their search is
 * 81% good, and tunes against a number that does not mean that.
 */
export const SCORE_NOTE = 'Scores rank these results against each other. They are not '
  + 'percentages and do not compare across projects or indexes — the order is the answer.'

/** Four decimals: enough to separate near-ties, few enough to scan. */
export const formatScore = (score: number): string =>
  Number.isFinite(score) ? score.toFixed(4) : '—'

/**
 * Metadata, on one line, without pretending to render arbitrary JSON.
 *
 * A customer puts whatever they like in here. Showing it as a compact
 * key=value line beats a pretty-printed object that pushes every other
 * result off the screen.
 */
export function summariseMetadata(metadata: Record<string, unknown> | null): string {
  if (!metadata) return '—'
  const parts = Object.entries(metadata).map(([k, v]) => `${k}=${short(v)}`)
  return parts.length === 0 ? '—' : parts.join(' · ')
}

function short(value: unknown): string {
  const text = typeof value === 'string' ? value : JSON.stringify(value) ?? String(value)
  return text.length > 40 ? `${text.slice(0, 39)}…` : text
}

/**
 * Why this page cannot show what is in the index.
 *
 * Stated on the page rather than left as a puzzle: a browse view is not
 * missing work, it is not offered by the index.
 */
export const NO_BROWSE_NOTE = 'A vector index cannot be listed — there is no "show me '
  + 'everything" to ask it. Searching is the only way to see what is in one.'
